import React, { useState, useEffect } from 'react';
import api from '../api';
import { toast } from 'react-toastify';
import CryptoJS from 'crypto-js';

const FreeGameScheduler = () => {
  const [activeTab, setActiveTab] = useState('single');
  const [schedules, setSchedules] = useState([]);
  const [scheduledDateTime, setScheduledDateTime] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [editingScheduleId, setEditingScheduleId] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [dateConverterOpen, setDateConverterOpen] = useState(false);
  const [englishDate, setEnglishDate] = useState('');
  const [amharicDate, setAmharicDate] = useState({
    day: '',
    month: '',
    year: ''
  });
  const [bulkScheduleData, setBulkScheduleData] = useState({
    startDate: '',
    endDate: '',
    timeSlots: [],
    selectedDays: []
  });

  const fetchServerTime = async () => {
    try {
      const response = await api.get(`${API}/current`);
      const serverTime = new Date(response.currentTime);
      
      // Ensure we're working with UTC+3
      const utcPlus3Offset = 3 * 60; // 3 hours in minutes
      const localOffset = serverTime.getTimezoneOffset(); // Local timezone offset in minutes
      const totalOffsetMs = (utcPlus3Offset + localOffset) * 60 * 1000; // Convert to milliseconds
      
      return new Date(serverTime.getTime() + totalOffsetMs);
    } catch (error) {
      console.error('Error fetching server time:', error);
      toast.error('Failed to sync time with server');
      
      const localTime = new Date();
      const utcPlus3Offset = 3 * 60;
      const localOffset = localTime.getTimezoneOffset();
      const totalOffsetMs = (utcPlus3Offset + localOffset) * 60 * 1000;
      
      return new Date(localTime.getTime() + totalOffsetMs);
    }
  };

  const fetchFreeGameHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await api.get(`${API}/admin/free-game/history`);
      setHistoryData(response || []);
    } catch (error) {
      toast.error('የነፃ ጨዋታ ታሪክን ለማግኘት አልተሳካም');
      console.error('Error fetching free game history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };
  
  const handleCancelAll = async () => {
    if (!window.confirm('እርግጠኛ ነዎት ሁሉንም ነፃ ጨዋታዎች መሰረዝ ይፈልጋሉ?')) {
      return;
    }
    
    setIsLoading(true);
    try {
      await api.post(`${API}/admin/free-game/cancel-all`);
      toast.success('ሁሉም ነፃ ጨዋታዎች በተሳካ ሁኔታ ተሰርዘዋል');
      fetchSchedules();
    } catch (error) {
      toast.error('ነፃ ጨዋታዎችን ለመሰረዝ አልተሳካም');
      console.error('Error cancelling all free games:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const FreeGameHistoryModal = ({ isOpen, onClose, data, loading }) => {
    if (!isOpen) return null;
    const getUsedTime = (item) => {
      if (item.status !== 'used') return null;
      const timeToUse = item.usedAt || (item.usedInGameId && item.usedInGameId.createdAt);
      if (!timeToUse) return 'ጊዜው አልተገኘም';
      return formatDateTime(timeToUse);
    };
  
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <div className="bg-teal-900 rounded-xl w-full max-w-2xl shadow-2xl">
          {/* Header */}
          <div className="px-4 py-3 border-b border-teal-800 flex justify-between items-center">
            <h2 className="text-lg text-white font-semibold">የነፃ ጨዋታ ታሪክ</h2>
            <button
              onClick={onClose}
              className="text-teal-400 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Content */}
          <div className="p-4 overflow-auto max-h-[60vh]">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
              </div>
            ) : data.length === 0 ? (
              <div className="text-center py-8 text-teal-400">ምንም ታሪክ አልተገኘም</div>
            ) : (
              <div className="space-y-2">
                {data.map((item) => (
                  <div 
                    key={item._id} 
                    className={`bg-teal-800/50 rounded-lg p-3 border border-gray-700/50 hover:bg-gray-800 transition-colors
                      ${item.status === 'used' ? 'border-green-800/50' : 
                        item.status === 'void' ? 'border-red-800/50' : 
                        item.status === 'cancelled' ? 'border-yellow-800/50' : ''}`}
                  >
                    <div className="flex justify-between items-start gap-4">
                      <div>
                        <p className="text-white/90 text-sm">
                          {formatDateTime(item.scheduledDateTime)}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`px-2 py-0.5 rounded-full text-xs
                            ${item.status === 'used' ? 'bg-green-900/50 text-green-400' : 
                              item.status === 'void' ? 'bg-red-900/50 text-red-400' : 
                              item.status === 'cancelled' ? 'bg-yellow-900/50 text-yellow-400' :
                              'bg-teal-900/50 text-teal-400'}`}>
                            {item.status === 'scheduled' ? 'የተያዘ' :
                             item.status === 'used' ? 'የተጠቀመ' :
                             item.status === 'cancelled' ? 'የተሰረዘ' :
                             item.status === 'void' ? 'ጊዜው ያለፈበት' :
                             item.status}
                          </span>
                        </div>
                      </div>
                      {item.status === 'used' && (
                        <div className="text-xs text-gray-400">
                          <p>የተጠቀመበት: {getUsedTime(item)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const [randomScheduleData, setRandomScheduleData] = useState({
    startDateTime: '',
    endDateTime: '',
    numberOfGames: 1,
    minGapMinutes: 30
  });

  const handleBulkSchedule = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const serverTime = await fetchServerTime();
      const utcPlus3Offset = 3 * 60;
      const localOffset = serverTime.getTimezoneOffset();
      const totalOffsetMs = (utcPlus3Offset + localOffset) * 60 * 1000;

      const adjustedBulkScheduleData = {
        ...bulkScheduleData,
        startDate: new Date(new Date(bulkScheduleData.startDate).getTime() + totalOffsetMs).toISOString(),
        endDate: new Date(new Date(bulkScheduleData.endDate).getTime() + totalOffsetMs).toISOString()
      };

      await api.post(`${API}/free-game/bulk-schedule`, adjustedBulkScheduleData);
      toast.success('ነፃ ጨዋታዎች በተሳካ ሁኔታ ተመድበዋል');
      setBulkScheduleData({
        startDate: '',
        endDate: '',
        timeSlots: [],
        selectedDays: []
      });
      fetchSchedules();
    } catch (error) {
      toast.error(error.response?.message || 'ነፃ ጨዋታዎችን ለመመደብ አልተሳካም');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRandomSchedule = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const serverTime = await fetchServerTime();
      const utcPlus3Offset = 3 * 60;
      const localOffset = serverTime.getTimezoneOffset();
      const totalOffsetMs = (utcPlus3Offset + localOffset) * 60 * 1000;

      const adjustedRandomScheduleData = {
        ...randomScheduleData,
        startDateTime: new Date(new Date(randomScheduleData.startDateTime).getTime() + totalOffsetMs).toISOString(),
        endDateTime: new Date(new Date(randomScheduleData.endDateTime).getTime() + totalOffsetMs).toISOString()
      };
      await api.post(`${API}/free-game/random-schedule`, adjustedRandomScheduleData);
      toast.success('ነፃ ጨዋታዎች በዘፈቀደ በተሳካ ሁኔታ ተመድበዋል');
      setRandomScheduleData({
        startDateTime: '',
        endDateTime: '',
        numberOfGames: 1,
        minGapMinutes: 30
      });
      fetchSchedules();
    } catch (error) {
      toast.error(error.response?.message || 'ነፃ ጨዋታዎችን በዘፈቀደ ለመመደብ አልተሳካም');
    } finally {
      setIsLoading(false);
    }
  };

  const getApiUrl = () => {
    const hashedApiVersion = localStorage.getItem('apiVersion');
    if (hashedApiVersion) {
      const version2Hash = CryptoJS.SHA256('2').toString();
      if (hashedApiVersion === version2Hash) {
        return 'https://phoenix-bingo-2.onrender.com/api';
      }
    }
    return process.env.REACT_APP_API;
  };

  const API = getApiUrl();

  useEffect(() => {
    fetchSchedules();
  }, []);

  const handleTimeSlotChange = (timeSlot) => {
    setBulkScheduleData(prev => ({
      ...prev,
      timeSlots: prev.timeSlots.includes(timeSlot)
        ? prev.timeSlots.filter(t => t !== timeSlot)
        : [...prev.timeSlots, timeSlot]
    }));
  };

  // Day selection handler for bulk scheduling
  const handleDaySelection = (day) => {
    setBulkScheduleData(prev => ({
      ...prev,
      selectedDays: prev.selectedDays.includes(day)
        ? prev.selectedDays.filter(d => d !== day)
        : [...prev.selectedDays, day]
    }));
  };

  const fetchSchedules = async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`${API}/admin/free-game/schedules`);
      setSchedules(response || []);
    } catch (error) {
      toast.error('ሰሌዳዎችን ለማግኘት አልተሳካም');
      console.error('Error fetching schedules:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSchedule = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const serverTime = await fetchServerTime();
      const scheduledTime = new Date(scheduledDateTime);
      
      const utcPlus3Offset = 3 * 60;
      const localOffset = serverTime.getTimezoneOffset();
      const totalOffsetMs = (utcPlus3Offset + localOffset) * 60 * 1000;
      
      const adjustedScheduledTime = new Date(scheduledTime.getTime() + totalOffsetMs);
      await api.post(`${API}/free-game/schedule`, {
        scheduledDateTime: adjustedScheduledTime.toISOString()
      });
      toast.success('ነፃ ጨዋታ በተሳካ ሁኔታ ተመድቧል');
      setScheduledDateTime('');
      fetchSchedules();
    } catch (error) {
      toast.error(error.response?.message || 'ነፃ ጨዋታን ለመመደብ አልተሳካም');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = async (scheduleId) => {
    setIsLoading(true);
    try {
      const serverTime = await fetchServerTime();
      const scheduledTime = new Date(scheduledDateTime);
      
      const utcPlus3Offset = 3 * 60;
      const localOffset = serverTime.getTimezoneOffset();
      const totalOffsetMs = (utcPlus3Offset + localOffset) * 60 * 1000;
      
      const adjustedScheduledTime = new Date(scheduledTime.getTime() + totalOffsetMs);

      await api.put(`${API}/free-game/schedule/${scheduleId}`, {
        scheduledDateTime: adjustedScheduledTime.toISOString()
      });
      toast.success('ሰሌዳው በተሳካ ሁኔታ ተዘምኗል');
      setEditingScheduleId(null);
      setScheduledDateTime('');
      fetchSchedules();
    } catch (error) {
      toast.error(error.response?.message || 'ሰሌዳውን ለማዘመን አልተሳካም');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = async (scheduleId) => {
    if (!window.confirm('እርግጠኛ ነዎት ይህን ነፃ ጨዋታ ሰሌዳ መሰረዝ ይፈልጋሉ?')) {
      return;
    }
    setIsLoading(true);
    try {
      await api.delete(`${API}/free-game/schedule/${scheduleId}`);
      toast.success('ሰሌዳው በተሳካ ሁኔታ ተሰርዟል');
      fetchSchedules();
    } catch (error) {
      toast.error(error.response?.message || 'ሰሌዳውን ለመሰረዝ አልተሳካም');
    } finally {
      setIsLoading(false);
    }
  };

  const convertToEthiopianTime = (hours, minutes) => {
    let ethiopianHours = hours - 6;
    let period;

    if (hours >= 6 && hours < 12) {
      ethiopianHours = hours - 6;
      period = 'ሰዓት ጥዋት';
    } else if (hours >= 12 && hours < 18) {
        ethiopianHours = hours - 6;
        period = 'ሰዓት ቀን';
    } else if (hours >= 18 && hours < 24) {
        ethiopianHours = hours - 18;
        period = 'ሰዓት ማታ';
    } else {
        ethiopianHours = hours + 6;
        period = 'ሰዓት ሌሊት';
    }
    if (ethiopianHours === 0) {
        ethiopianHours = 12;
    }

    const formattedMinutes = minutes.toString().padStart(2, '0');
    return `${ethiopianHours}:${formattedMinutes} ${period}`;
  };

  const convertFromEthiopianTime = (ethiopianHour, period) => {
    switch(period) {
      case 'ጥዋት':
        return ethiopianHour + 6; // 1-6 ጥዋት -> 7-12 standard
      case 'ቀን':
        return ethiopianHour + 6; // 6-12 ቀን -> 12-18 standard
      case 'ማታ':
        return ethiopianHour + 18; // 1-6 ማታ -> 19-24 standard
      case 'ሌሊት':
        return ethiopianHour - 6; // 7-12 ሌሊት -> 1-6 standard
      default:
        return ethiopianHour;
    }
  };

  const formatDateTime = (dateStr, dateOnly = false) => {
    if (!dateStr) return '';
    
    const date = new Date(dateStr);
    const days = ['እሁድ', 'ሰኞ', 'ማክሰኞ', 'ረቡዕ', 'ሐሙስ', 'አርብ', 'ቅዳሜ'];
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    // Adjust date for Ethiopian time (subtract 6 hours)
    const ethiopianDate = new Date(date.getTime() - (6 * 60 * 60 * 1000));

    const year = ethiopianDate.getFullYear();
    const month = months[ethiopianDate.getMonth()];
    const day = ethiopianDate.getDate();
    const dayOfWeek = days[ethiopianDate.getDay()];
    const hours = date.getHours();
    const minutes = date.getMinutes();

    if (dateOnly) {
        return `${dayOfWeek}፣ ${month} ${day}፣ ${year}`;
    }

    const ethiopianTime = convertToEthiopianTime(hours, minutes);
    return `${dayOfWeek} ${month} ${day} ${year} ${ethiopianTime}`;
  };

  const formatDateTimeForInput = (dateStr) => {
    const date = new Date(dateStr);
    const offset = date.getTimezoneOffset();
    const adjustedDate = new Date(date.getTime() - (offset * 60 * 1000));
    return adjustedDate.toISOString().slice(0, 16);
  };

  const getEthiopianDate = (gregorianDate) => {
    const ethiopianMonths = [
      'መስከረም', 'ጥቅምት', 'ህዳር', 'ታህሳስ', 'ጥር', 'የካቲት',
      'መጋቢት', 'ሚያዚያ', 'ግንቦት', 'ሰኔ', 'ሐምሌ', 'ነሐሴ', 'ጷግሜ'
    ];

    try {
      const year = gregorianDate.getFullYear();
      const month = gregorianDate.getMonth() + 1;
      const day = gregorianDate.getDate();
  
      const isLeapYear = (year) => {
        return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
      };
  
      const newYearDay = isLeapYear(year) ? 12 : 11;
  
      let ethYear = year - 8;
      if (month < 9 || (month === 9 && day < newYearDay)) {
        ethYear = year - 8;
      } else {
        ethYear = year - 7;
      }
  
      const gregorianMonths = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
      if (isLeapYear(year)) {
        gregorianMonths[1] = 29;
      }
  
      let daysSinceNewYear = 0;
      if (month > 9 || (month === 9 && day >= newYearDay)) {
        if (month === 9) {
          daysSinceNewYear = day - newYearDay;
        } else {
          daysSinceNewYear = gregorianMonths[8] - newYearDay;
          for (let m = 9; m < month - 1; m++) {
            daysSinceNewYear += gregorianMonths[m];
          }
          daysSinceNewYear += day;
        }
      } else {
        daysSinceNewYear = gregorianMonths[8] - newYearDay;
        for (let m = 9; m < 12; m++) {
          daysSinceNewYear += gregorianMonths[m];
        }
        for (let m = 0; m < month - 1; m++) {
          daysSinceNewYear += gregorianMonths[m];
        }
        daysSinceNewYear += day;
      }
  
      let ethMonth = Math.floor(daysSinceNewYear / 30) + 1;
      let ethDay = (daysSinceNewYear % 30) + 1;
  
      if (ethMonth > 13) {
        ethMonth = 13;
        ethDay = daysSinceNewYear - (12 * 30);
      }
  
      return {
        year: ethYear,
        month: ethMonth,
        monthName: ethiopianMonths[ethMonth - 1],
        day: ethDay
      };
    } catch (error) {
      console.error('Error converting to Ethiopian date:', error);
      return null;
    }
  };

  const getNext45Days = () => {
    const days = [];
    const today = new Date();
    
    for (let i = 0; i < 45; i++) {
      const currentDate = new Date(today);
      currentDate.setDate(today.getDate() + i);
      
      const ethDate = getEthiopianDate(currentDate);
      if (ethDate) {
        days.push({
          gregorian: currentDate.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }),
          ethiopian: {
            ...ethDate,
            weekday: currentDate.toLocaleDateString('am-ET', { weekday: 'long' })
          }
        });
      }
    }
    return days;
  };

  const DateConverter = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    const next45Days = getNext45Days();

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <div className="w-50 bg-gray-900 rounded-xl w-full max-w-4xl shadow-2xl">
          <div className="px-4 py-3 border-b border-gray-800 flex justify-between items-center">
            <h2 className="text-lg text-white font-semibold">የ45 ቀናት ዝርዝር</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="p-4 overflow-auto max-h-[80vh]">
            <div className="grid gap-2">
              {next45Days.map((date, index) => (
                <div 
                  key={index}
                  className="bg-gray-800/50 p-3 rounded-lg border border-gray-700/50 hover:bg-gray-800/70 transition-colors"
                >
                  <div className="flex justify-between items-center">
                    <div className="text-white">
                      <span className="text-gray-400"></span> {date.gregorian}
                    </div>
                    <div className="text-white text-right">
                      <span className="text-gray-400"></span> {date.ethiopian.weekday}፣{date.ethiopian.monthName} {date.ethiopian.day}፣ {date.ethiopian.year} ዓ.ም.
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="container mx-auto px-2 py-1">
      <div className="bg-teal-900/20 rounded-lg py-2 px-4">
        <div className='flex justify-content-between mb-8'>
          <h2 className="text-2xl text-white font-bold mb-6">ነፃ ጨዋታ መርሐግብር</h2>
          <button
            onClick={() => setDateConverterOpen(true)}
            className="bg-teal-300 text-white px-4 py-1 rounded hover:bg-teal-700"
          >
            ቀን ለውጥ
          </button>
        </div>
        <div className="flex mb-6 bg-gray-800 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('single')}
            className={`flex-1 py-2 px-4 rounded ${
              activeTab === 'single' ? 'bg-teal-600 text-white' : 'text-gray-400'
            }`}
          >
            አንድ ጨዋታ
          </button>
          <button
            onClick={() => setActiveTab('bulk')}
            className={`flex-1 py-2 px-4 rounded ${
              activeTab === 'bulk' ? 'bg-teal-600 text-white' : 'text-gray-400'
            }`}
          >
            ብዙ ጨዋታዎች
          </button>
          <button
            onClick={() => setActiveTab('random')}
            className={`flex-1 py-2 px-4 rounded ${
              activeTab === 'random' ? 'bg-teal-600 text-white' : 'text-gray-400'
            }`}
          >
            በዘፈቀደ
          </button>
        </div>
        
        <DateConverter 
          isOpen={dateConverterOpen}
          onClose={() => {
            setDateConverterOpen(false);
            setEnglishDate('');
            setAmharicDate({ day: '', month: '', year: '' });
          }}
        />
        {activeTab === 'single' && (
          <>
            <form onSubmit={handleSchedule} className="mb-8">
              <div className="flex flex-row gap-2">
                  <input
                    type="date"
                    value={scheduledDateTime.split('T')[0]}
                    onChange={(e) => setScheduledDateTime(prev => `${e.target.value}T${prev.split('T')[1] || '00:00'}`)}
                    className="w-full bg-gray-800 text-white p-2 rounded mb-2 cursor-pointer"
                    onClick={(e) => e.target.showPicker()}
                    required
                  />
                  <select
  value={scheduledDateTime.split('T')[1]?.split(':')[0] || ''}
  onChange={(e) => {
    const hour = e.target.value.padStart(2, '0');
    const minutes = scheduledDateTime.split('T')[1]?.split(':')[1] || '00';
    setScheduledDateTime(prev => `${prev.split('T')[0]}T${hour}:${minutes}`);
  }}
  className="w-full bg-gray-800 text-white p-2 rounded mb-2"
  required
>
  <option value="">ሰዓት ይምረጡ</option>
  <optgroup label="ጥዋት">
    {[1,2,3,4,5,6].map(hour => (
      <option key={hour} value={convertFromEthiopianTime(hour, 'ጥዋት').toString().padStart(2, '0')}>
        {`${hour}:00 ጥዋት`}
      </option>
    ))}
  </optgroup>
  <optgroup label="ቀን">
    {[6,7,8,9,10,11,12].map(hour => (
      <option key={hour} value={convertFromEthiopianTime(hour, 'ቀን').toString().padStart(2, '0')}>
        {`${hour}:00 ቀን`}
      </option>
    ))}
  </optgroup>
  <optgroup label="ማታ">
    {[1,2,3,4,5,6].map(hour => (
      <option key={hour} value={convertFromEthiopianTime(hour, 'ማታ').toString().padStart(2, '0')}>
        {`${hour}:00 ማታ`}
      </option>
    ))}
  </optgroup>
  <optgroup label="ሌሊት">
    {[7,8,9,10,11,12].map(hour => (
      <option key={hour} value={convertFromEthiopianTime(hour, 'ሌሊት').toString().padStart(2, '0')}>
        {`${hour}:00 ሌሊት`}
      </option>
    ))}
  </optgroup>
                  </select>
                  <select
                    value={scheduledDateTime.split('T')[1]?.split(':')[1] || ''}
                    onChange={(e) => {
                      const hour = scheduledDateTime.split('T')[1]?.split(':')[0] || '00';
                      setScheduledDateTime(prev => `${prev.split('T')[0]}T${hour}:${e.target.value}`);
                    }}
                    className="w-full bg-gray-800 text-white p-2 rounded mb-2"
                    required
                  >
                    <option value="">ደቂቃ ይምረጡ</option>
                    {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map(minute => (
                      <option key={minute} value={minute.toString().padStart(2, '0')}>
                        {minute} ደቂቃ
                      </option>
                    ))}
                  </select>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="text-white px-2 py-0 max-h-12 min-h-10 bg-teal-600"
                  style={{ backgroundColor: '#2DD4BF', color: 'white'}}
                >
                  ነፃ ጨዋታ ያስይዙ
                </button>
              </div>
            </form>
          </>
        )}

        {activeTab === 'bulk' && (
          <form onSubmit={handleBulkSchedule} className="mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-white mb-2">ከ ቀን</label>
                <input
                  type="date"
                  value={bulkScheduleData.startDate}
                  onClick={(e) => e.target.showPicker()}
                  onChange={(e) => setBulkScheduleData(prev => ({
                    ...prev,
                    startDate: e.target.value
                  }))}
                  className="w-full bg-gray-800 text-white p-2 rounded"
                  required
                />
              </div>
              <div>
                <label className="block text-white mb-2">እስከ ቀን</label>
                <input
                  type="date"
                  value={bulkScheduleData.endDate}
                  onClick={(e) => e.target.showPicker()}
                  onChange={(e) => setBulkScheduleData(prev => ({
                    ...prev,
                    endDate: e.target.value
                  }))}
                  className="w-full bg-gray-800 text-white p-2 rounded"
                  required
                />
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-white mb-2">ቀናት ይምረጡ</label>
              <div className="flex flex-wrap gap-2">
                {['እሁድ', 'ሰኞ', 'ማክሰኞ', 'ረቡዕ', 'ሐሙስ', 'አርብ', 'ቅዳሜ'].map((day, index) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => handleDaySelection(index)}
                    className={`px-4 py-2 rounded ${
                      bulkScheduleData.selectedDays.includes(index)
                        ? 'bg-teal-600 text-white'
                        : 'bg-gray-700 text-gray-300'
                    }`}
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-white mb-2">ሰዓታት ይምረጡ</label>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <div className="flex flex-wrap gap-2">
                    <div className='flex gap-0.5 flex-wrap'>
                      {[1,2,3,4,5].map(hour => (
                        <button
                          key={`morning-${hour}`}
                          type="button"
                          onClick={() => handleTimeSlotChange(`${convertFromEthiopianTime(hour, 'ጥዋት')}:00`)}
                          className={`p-1 text-sm ${
                            bulkScheduleData.timeSlots.includes(`${convertFromEthiopianTime(hour, 'ጥዋት')}:00`)
                              ? 'bg-teal-600 text-white'
                              : 'bg-gray-700 text-gray-300'
                          }`}
                        >
                          {`${hour}:00 ጥዋት`}
                        </button>
                      ))}
                      {[6,7,8,9,10,11,12].map(hour => (
                        <button
                          key={`day-${hour}`}
                          type="button"
                          onClick={() => handleTimeSlotChange(`${convertFromEthiopianTime(hour, 'ቀን')}:00`)}
                          className={`p-1 text-sm ${
                            bulkScheduleData.timeSlots.includes(`${convertFromEthiopianTime(hour, 'ቀን')}:00`)
                              ? 'bg-teal-600 text-white'
                              : 'bg-gray-700 text-gray-300'
                          }`}
                        >
                          {`${hour}:00 ቀን`}
                        </button>
                      ))}
                    </div>
                    <div className='flex gap-0.5 flex-wrap'>
                      {[1,2,3,4,5].map(hour => (
                        <button
                          key={`evening-${hour}`}
                          type="button"
                          onClick={() => handleTimeSlotChange(`${convertFromEthiopianTime(hour, 'ማታ')}:00`)}
                          className={`p-1 text-sm ${
                            bulkScheduleData.timeSlots.includes(`${convertFromEthiopianTime(hour, 'ማታ')}:00`)
                              ? 'bg-teal-600 text-white'
                              : 'bg-gray-700 text-gray-300'
                          }`}
                        >
                          {`${hour}:00 ማታ`}
                        </button>
                      ))}
                      {[6,7,8,9,10,11,12].map(hour => (
                        <button
                          key={`night-${hour}`}
                          type="button"
                          onClick={() => handleTimeSlotChange(`${convertFromEthiopianTime(hour, 'ሌሊት')}:00`)}
                          className={`p-1 text-sm  ${
                            bulkScheduleData.timeSlots.includes(`${convertFromEthiopianTime(hour, 'ሌሊት')}:00`)
                              ? 'bg-teal-600 text-white'
                              : 'bg-gray-700 text-gray-300'
                          }`}
                        >
                          {`${hour}:00 ሌሊት`}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="mt-6 w-25 mx-auto bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50"
              style={{ backgroundColor: '#2DD4BF', color: 'white'}}
            >
              {isLoading ? 'በመመደብ ላይ...' : 'ነፃ ጨዋታዎችን ያስይዙ'}
            </button>
          </form>
        )}

        {activeTab === 'random' && (
          <form onSubmit={handleRandomSchedule} className="mb-8">
            {/* Start Date/Time */}
            <div className="mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-1">ከቀን</label>
                  <input
                    type="date"
                    value={randomScheduleData.startDateTime.split('T')[0]}
                    onClick={(e) => e.target.showPicker()}
                    onChange={(e) => setRandomScheduleData(prev => ({
                      ...prev,
                      startDateTime: `${e.target.value}T${prev.startDateTime.split('T')[1] || '00:00'}`
                    }))}
                    className="w-full bg-gray-800 text-white p-2 rounded"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">ከሰዓት</label>
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={randomScheduleData.startDateTime.split('T')[1]?.split(':')[0] || ''}
                      onChange={(e) => {
                        const minutes = randomScheduleData.startDateTime.split('T')[1]?.split(':')[1] || '00';
                        setRandomScheduleData(prev => ({
                          ...prev,
                          startDateTime: `${prev.startDateTime.split('T')[0]}T${e.target.value}:${minutes}`
                        }));
                      }}
                      className="w-full bg-gray-800 text-white p-2 rounded"
                      required
                    >
                      <option value="">ሰዓት ይምረጡ</option>
                      <optgroup label="ጥዋት">
                        {[1,2,3,4,5,6].map(hour => (
                          <option key={hour} value={(hour + 6).toString().padStart(2, '0')}>
                            {hour}:00 ጥዋት
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="ቀን">
                        {[6,7,8,9,10,11,12].map(hour => (
                          <option key={hour} value={(hour + 6).toString().padStart(2, '0')}>
                            {hour}:00 ቀን
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="ማታ">
                        {[12,1,2,3,4,5,6].map(hour => (
                          <option key={hour} value={(hour === 12 ? 18 : hour + 18).toString().padStart(2, '0')}>
                            {hour}:00 ማታ
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="ሌሊት">
                        {[6,7,8,9,10,11,12].map(hour => (
                          <option key={hour} value={(hour - 6).toString().padStart(2, '0')}>
                            {hour}:00 ሌሊት
                          </option>
                        ))}
                      </optgroup>
                    </select>
                    <select
                      value={randomScheduleData.startDateTime.split('T')[1]?.split(':')[1] || ''}
                      onChange={(e) => {
                        const hour = randomScheduleData.startDateTime.split('T')[1]?.split(':')[0] || '00';
                        setRandomScheduleData(prev => ({
                          ...prev,
                          startDateTime: `${prev.startDateTime.split('T')[0]}T${hour}:${e.target.value}`
                        }));
                      }}
                      className="w-full bg-gray-800 text-white p-2 rounded"
                      required
                    >
                      <option value="">ደቂቃ ይምረጡ</option>
                      {[0, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map(minute => (
                        <option key={minute} value={minute.toString().padStart(2, '0')}>
                          {minute} ደቂቃ
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* End Date/Time */}
            <div className="mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-1">እስከ ቀን</label>
                  <input
                    type="date"
                    value={randomScheduleData.endDateTime.split('T')[0]}
                    onClick={(e) => e.target.showPicker()}
                    onChange={(e) => setRandomScheduleData(prev => ({
                      ...prev,
                      endDateTime: `${e.target.value}T${prev.endDateTime.split('T')[1] || '00:00'}`
                    }))}
                    className="w-full bg-gray-800 text-white p-2 rounded"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">እስከ ሰዓት</label>
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={randomScheduleData.endDateTime.split('T')[1]?.split(':')[0] || ''}
                      onChange={(e) => {
                        const minutes = randomScheduleData.endDateTime.split('T')[1]?.split(':')[1] || '00';
                        setRandomScheduleData(prev => ({
                          ...prev,
                          endDateTime: `${prev.endDateTime.split('T')[0]}T${e.target.value}:${minutes}`
                        }));
                      }}
                      className="w-full bg-gray-800 text-white p-2 rounded"
                      required
                    >
                      <option value="">ሰዓት ይምረጡ</option>
                      <optgroup label="ጥዋት">
                        {[1,2,3,4,5,6].map(hour => (
                          <option key={hour} value={(hour + 6).toString().padStart(2, '0')}>
                            {hour}:00 ጥዋት
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="ቀን">
                        {[6,7,8,9,10,11,12].map(hour => (
                          <option key={hour} value={(hour + 6).toString().padStart(2, '0')}>
                            {hour}:00 ቀን
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="ማታ">
                        {[12,1,2,3,4,5,6].map(hour => (
                          <option key={hour} value={(hour === 12 ? 18 : hour + 18).toString().padStart(2, '0')}>
                            {hour}:00 ማታ
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="ሌሊት">
                        {[6,7,8,9,10,11,12].map(hour => (
                          <option key={hour} value={(hour - 6).toString().padStart(2, '0')}>
                            {hour}:00 ሌሊት
                          </option>
                        ))}
                      </optgroup>
                    </select>
                    <select
                      value={randomScheduleData.endDateTime.split('T')[1]?.split(':')[1] || ''}
                      onChange={(e) => {
                        const hour = randomScheduleData.endDateTime.split('T')[1]?.split(':')[0] || '00';
                        setRandomScheduleData(prev => ({
                          ...prev,
                          endDateTime: `${prev.endDateTime.split('T')[0]}T${hour}:${e.target.value}`
                        }));
                      }}
                      className="w-full bg-gray-800 text-white p-2 rounded"
                      required
                    >
                      <option value="">ደቂቃ ይምረጡ</option>
                      {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map(minute => (
                        <option key={minute} value={minute.toString().padStart(2, '0')}>
                          {minute} ደቂቃ
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Game Settings */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-white mb-2">የጨዋታዎች ብዛት</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={randomScheduleData.numberOfGames}
                  onChange={(e) => setRandomScheduleData(prev => ({
                    ...prev,
                    numberOfGames: parseInt(e.target.value)
                  }))}
                  className="w-full bg-gray-800 text-white p-2 rounded"
                  required
                />
              </div>
              <div>
                <label className="block text-white mb-2">ከጨዋታ እስከ ጨዋታ ያለው ጊዜ (በደቂቃ)</label>
                <select
                  value={randomScheduleData.minGapMinutes}
                  onChange={(e) => setRandomScheduleData(prev => ({
                    ...prev,
                    minGapMinutes: parseInt(e.target.value)
                  }))}
                  className="w-full bg-gray-800 text-white p-2 rounded"
                  required
                >
                  <option value="10">10 ደቂቃ</option>
                  <option value="15">15 ደቂቃ</option>
                  <option value="30">30 ደቂቃ</option>
                  <option value="45">45 ደቂቃ</option>
                  <option value="60">1 ሰዓት</option>
                  <option value="75">1 ሰዓት 15 ደቂቃ</option>
                  <option value="90">1 ሰዓት 30 ደቂቃ</option>
                  <option value="105">1 ሰዓት 45 ደቂቃ</option>
                  <option value="120">2 ሰዓት</option>
                </select>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              style={{ backgroundColor: '#2DD4BF', color: 'white'}}
              className="mt-6 bg-teal-600 text-white px-6 py-2 rounded hover:bg-teal-700 disabled:opacity-50"
            >
              {isLoading ? 'በመመደብ ላይ...' : 'በዘፈቀደ ያስይዙ'}
            </button>
          </form>
        )}

        {/* Schedules List */}
        <div className="space-y-2">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl text-white font-semibold">የተያዙ ነፃ ጨዋታዎች</h3>
            <div className="flex gap-4">
            <button
              onClick={() => {
                setShowHistoryModal(true);
                fetchFreeGameHistory();
              }}
              className="bg-teal-400 text-white px-4 py-2 rounded hover:bg-teal-700"
            >
              ታሪክ
            </button>
            <button
              onClick={handleCancelAll}
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50"
              disabled={isLoading || schedules.length === 0}
            >
              ሁሉንም ሰርዝ
            </button>
            </div>
          </div>
          {isLoading ? (
            <div className="text-center py-4 text-white">Loading...</div>
          ) : schedules.length === 0 ? (
            <div className="text-gray-400">ምንም የተያዙ ነፃ ጨዋታዎች የሉም</div>
          ) : (
            schedules.map((schedule) => (
              <div
                key={schedule._id}
                className="bg-gray-800 rounded px-4 py-2 flex flex-col md:flex-row items-center justify-between gap-0.5"
              >
                <div className="text-white">
                  <p className="font-semibold">
                    {formatDateTime(schedule.scheduledDateTime)}
                  </p>
                  <p className="text-sm text-gray-400">
                    ሁኔታ: {schedule.status === 'scheduled' ? 'የተያዘ' : 
                           schedule.status === 'used' ? 'የተጠቀመ' : 
                           schedule.status === 'cancelled' ? 'የተሰረዘ' : schedule.status}
                  </p>
                </div>
                <div className="flex gap-2">
                  {editingScheduleId === schedule._id ? (
                    <>
                      <div className="flex-1">
                        <input
                          type="date"
                          value={scheduledDateTime.split('T')[0]}
                          onChange={(e) => setScheduledDateTime(prev => `${e.target.value}T${prev.split('T')[1] || '00:00'}`)}
                          className="w-full bg-gray-700 text-white p-2 rounded mb-2"
                          required
                        />
                        <select
  value={scheduledDateTime.split('T')[1]?.split(':')[0] || ''}
  onChange={(e) => {
    const hour = e.target.value.padStart(2, '0');
    const minutes = scheduledDateTime.split('T')[1]?.split(':')[1] || '00';
    setScheduledDateTime(prev => `${prev.split('T')[0]}T${hour}:${minutes}`);
  }}
  className="w-full bg-gray-800 text-white p-2 rounded mb-2"
  required
>
  <option value="">ሰዓት ይምረጡ</option>
  <optgroup label="ጥዋት">
    {[1,2,3,4,5,6].map(hour => (
      <option key={hour} value={convertFromEthiopianTime(hour, 'ጥዋት').toString().padStart(2, '0')}>
        {`${hour}:00 ጥዋት`}
      </option>
    ))}
  </optgroup>
  <optgroup label="ቀን">
    {[6,7,8,9,10,11,12].map(hour => (
      <option key={hour} value={convertFromEthiopianTime(hour, 'ቀን').toString().padStart(2, '0')}>
        {`${hour}:00 ቀን`}
      </option>
    ))}
  </optgroup>
  <optgroup label="ማታ">
    {[1,2,3,4,5,6].map(hour => (
      <option key={hour} value={convertFromEthiopianTime(hour, 'ማታ').toString().padStart(2, '0')}>
        {`${hour}:00 ማታ`}
      </option>
    ))}
  </optgroup>
  <optgroup label="ሌሊት">
    {[7,8,9,10,11,12].map(hour => (
      <option key={hour} value={convertFromEthiopianTime(hour, 'ሌሊት').toString().padStart(2, '0')}>
        {`${hour}:00 ሌሊት`}
      </option>
    ))}
  </optgroup>
                        </select>
                        <select
                          value={scheduledDateTime.split('T')[1]?.split(':')[1] || ''}
                          onChange={(e) => {
                            const hour = scheduledDateTime.split('T')[1]?.split(':')[0] || '00';
                            setScheduledDateTime(prev => `${prev.split('T')[0]}T${hour}:${e.target.value}`);
                          }}
                          className="w-full bg-gray-700 text-white p-2 rounded mt-2"
                          required
                        >
                          <option value="">ደቂቃ ይምረጡ</option>
                          {[0, 15, 30, 45].map(minute => (
                            <option key={minute} value={minute.toString().padStart(2, '0')}>
                              {minute} ደቂቃ
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="flex flex-col gap-2">
                        <button
                          onClick={() => handleEdit(schedule._id)}
                          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                        >
                          አስቀምጥ
                        </button>
                        <button
                          onClick={() => {
                            setEditingScheduleId(null);
                            setScheduledDateTime('');
                          }}
                          className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
                        >
                          ሰርዝ
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => {
                          setEditingScheduleId(schedule._id);
                          setScheduledDateTime(formatDateTimeForInput(schedule.scheduledDateTime));
                        }}
                        className="bg-yellow-600 text-white px-4 py-1 rounded hover:bg-yellow-700"
                      >
                        አስተካክል
                      </button>
                      <button
                        onClick={() => handleCancel(schedule._id)}
                        className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                      >
                        ሰርዝ
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      <FreeGameHistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        data={historyData}
        loading={historyLoading}
      />
    </div>
  );
  
};

export default FreeGameScheduler;